import { toast } from "sonner"
import { useState, useEffect, useContext, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { create } from "@github/webauthn-json/browser-ponyfill";

import Card from "@/components/card/Card";
import Header from "@/components/header/Header";
import FAQButton from "@/components/faq-button/faq-button";
import AlertRealOtps from "@/components/alert-real-otps/alert-real-otps";

import { homeInformationService } from "@/services/site-credentials/mobile/HomeInformationService";

import { RedirectResponse } from "@/models/site-credentials/mobile/RedirectResponse";

import {
  HomeRequest,
  HomeOption,
} from "@/models/site-credentials/HomeRequest";

import { Config } from "@/config/Config";
import { homeService } from "@/services/site-credentials/HomeService";
import { fidoService } from "@/services/continente-credentials/common/Fido2Service";

import { tryCatch } from "@/utils/try-catch";
import { divideEmail } from "@/utils/EmailDivider";

import { DigitalAssetContext } from "@/contexts/continente-credentials/DigitalAssetContext";
import useGetThemeMode from "@/hooks/useGetThemeMode";
import { useTheme } from "@/providers/theme-provider";
import { useOrigin } from "@/hooks/useOrigin";
import { otpService } from "@/services/continente-credentials/common/OtpService";
import { useGoToFaq } from "@/hooks/useGoToFaq";

export default function Home() {
  const history = useHistory();
  const { t } = useTranslation();
  const theme = useGetThemeMode();
  const { setTheme } = useTheme();
  const origin = useOrigin();
  const goToFaq = useGoToFaq();

  const ANCHOR_DISABLE_LOYALTYCARD_ASSOCIATION = "faq10";

  const [loading, setLoading] = useState(false);
  const [showAlertRealOtps, setShowAlertRealOtps] = useState(false);
  const [isPageLoaded, setIsPageLoaded] = useState(false);

  const location = useLocation<RedirectResponse>();
  const digitalAssetContext = useContext(DigitalAssetContext);

  const [userMobileNumber, setUserMobileNumber] = useState<string | null>(null);
  const [hasConsents, setHasConsents] = useState<boolean | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userLoyaltyCardNumber, setUserLoyaltyCardNumber] = useState<
    string | null
  >(null);
  const [userSelectedCountryCode, setUserSelectedCountryCode] = useState<
    string | null | undefined
  >(null);

  const clickEditPhonenumber = () => {
    requestFormSubmit({ homeOption: HomeOption.PhoneNumber });
  };

  const clickEditEmail = () => {
    requestFormSubmit({ homeOption: HomeOption.Email });
  };

  const clickEditLoyaltyCard = () => {
    requestFormSubmit({ homeOption: HomeOption.LoyaltyCard });
  };

  const clickEditPassword = () => {
    requestFormSubmit({ homeOption: HomeOption.Password });
  };

  const clickConsents = () => {
    requestFormSubmit({ homeOption: HomeOption.Consents });
  };

  const requestFormSubmit = async (homeRequest: HomeRequest) => {
    setLoading(true);

    const { data: response, error } = await tryCatch(homeService.nextPage(homeRequest));

    if (error) {
      toast.error(t("common.unauthorized_error"))
      setLoading(false);
      return;
    }

    switch (response.kind) {
      case "success":
        history.push(response.value.nextStep.substring(4), response.value);
        break;
      case "error":
        toast.error(t("common.unauthorized_error"))
        break;
      default:
        toast.error(t("common.unauthorized_error"))
        break;
    }

    setLoading(false);
  };

  useEffect(() => {
    if (location.state != null && location.state.responseCode != null) {
      let message = "";
      let type = "";
      switch (location.state.responseCode) {
        case "100000": {
          message = "site_credentials.association_success";
          type = "success";
          break;
        }
        case "100001": {
          message = "site_credentials.update_password.success_message";
          type = "success";
          break;
        }
        case "100002": {
          message =
            "site_credentials.loyaltycard_association.card_creation_success";
          type = "success";
          break;
        }
        case "100003": {
          message =
            "site_credentials.loyaltycard_association.card_association_success";
          type = "success";
          break;
        }
        case "300009": {
          message =
            "site_credentials.email_association.email_already_registered";
          type = "error";
          break;
        }
      }

      if (message != null) {
        toast(t(message));
      }
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (theme) {
      setTheme(theme)
    }
  }, [theme])

  useEffect(() => {
    const fetchData = async () => {
      const homeInformation = await handlerHomeInformation();

      if (homeInformation) {
        setUserEmail(homeInformation.usernameEmail);
        setUserMobileNumber(homeInformation.usernamePhoneNumber);
        setUserLoyaltyCardNumber(homeInformation.loyaltyCardNumber);
        setHasConsents(homeInformation.hasConsents)

        digitalAssetContext.updateDigitalAsset(homeInformation.digitalAsset);
        setIsPageLoaded(true);
        return;
      }

      setIsPageLoaded(false);
    };

    fetchData().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkFakeOtp = useCallback(async () => {
    if (origin !== "" && origin !== "https://login.continente.pt") {
      const useFakeOTPResult = await otpService.useFakeOTP();

      if (useFakeOTPResult.kind === "success") {
        if (
          useFakeOTPResult.value.isUsingFakeOtp != null &&
          !useFakeOTPResult.value?.isUsingFakeOtp
        ) {
          showWillSendRealOtps();
        }
      }
    }
  }, [origin]);

  const handlerHomeInformation = async () => {
    setLoading(true);

    const { data: response, error } = await tryCatch(homeInformationService.getHomeInfo(
      Config.SiteCredentials.homeInformationUrl
    ));

    let returnValue = null;

    if (error) {
      toast.error(t("common.unauthorized_error"))
      setLoading(false);
      return returnValue;
    }

    switch (response.kind) {
      case "success":
        returnValue = response.value;
        break;
      case "unauthorized":
        toast.error(t("common.unauthorized_error"))
        break;
      default:
        toast.error(t("common.unauthorized_error"))
        break;
    }

    setLoading(false);
    return returnValue;
  };

  useEffect(() => {
    checkFakeOtp();
  }, [origin]);

  const showWillSendRealOtps = () => {
    setShowAlertRealOtps(true);

    setTimeout(() => {
      setShowAlertRealOtps(false);
    }, 3000);
  };

  setTimeout(() => {
    setShowAlertRealOtps(false);
  }, 3000);

  const handleFidoOnClick = async () => {
    const fidoCredentialOptionsResult =
      await fidoService.getCredentialOptions();

    switch (fidoCredentialOptionsResult.kind) {
      case "success": {
        const credential = await create(fidoCredentialOptionsResult.value);
        const credentialRegistrationResponse =
          await fidoService.registerCredential(credential);

        switch (credentialRegistrationResponse.kind) {
          case "success":

          //TODO: error
        }
      }
      // TODO: error
    }
  };

  return (
    <div className="flex flex-col justify-self-center items-center gap-10 pb-2 w-full">
      {isPageLoaded && (
        <>
          <div className="flex flex-col gap-4 w-full">
            <Header
              showClose={false}
              showLoading={loading}
              title={t("site_credentials.credentials")}
            />
            <Card
              labelText={t("site_credentials.mobile_number")}
              informationText={userMobileNumber}
              onClick={clickEditPhonenumber}
              iconType={"editable"}
              type={"tel"}
              selectedCountryCode={userSelectedCountryCode}
            />
            <Card
              labelText={t("site_credentials.email")}
              informationText={divideEmail(userEmail)}
              onClick={clickEditEmail}
              iconType={userEmail ? "readonly" : "editable"}
            />
            <Card
              labelText={t("site_credentials.pw")}
              onClick={clickEditPassword}
              iconType={"editable"}
            />
          </div>
          <div className="flex flex-col gap-4 w-full">
            <Header title={t("site_credentials.other_informations")} />
            <div className="flex flex-col gap-3">
              <Card
                labelText={t("site_credentials.continente_card")}
                informationText={userLoyaltyCardNumber}
                onClick={
                  userLoyaltyCardNumber
                    ? () => goToFaq(ANCHOR_DISABLE_LOYALTYCARD_ASSOCIATION)
                    : clickEditLoyaltyCard
                }
                iconType={userLoyaltyCardNumber ? "readonly" : "editable"}
              />
              {digitalAssetContext.digitalAsset.passskeysEnabled && (
                <Card
                  labelText="Adicionar passkey"
                  onClick={handleFidoOnClick}
                  iconType={"editable"}
                />
              )}
              {
                hasConsents &&
                <Card
                  labelText={t("site_credentials.consents.title")}
                  onClick={clickConsents}
                  iconType={"editable"}
                />
              }
            </div>
          </div>
          <FAQButton
            onClick={() => goToFaq()}
          />
        </>
      )}

    </div>
  );
}
